import { Trans, useTranslation } from "react-i18next";
import { Modal } from "src/ui/shared/Modal/Modal";

import { useDeletePolicyMutation, useUnassignPolicyFromGroupsMutation } from "@dashboard/policies/api";
import { DialogContentText, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

type Props = {
    policy: API.PolicyViewModel;
    open: boolean;
    onClose: () => void;
    onDeleted: () => void;
};

const DeletePolicyModal = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);

    const [unassignPolicyFromGroups, { isLoading: isUnassigning }] = useUnassignPolicyFromGroupsMutation();
    const [deletePolicy, { isLoading: isDeleting }] = useDeletePolicyMutation();

    const assignedGroupIds = props.policy.groupIds;

    const onSubmit = async () => {
        let didUnassign = false;

        if (assignedGroupIds && assignedGroupIds.length > 0) {
            try {
                await unassignPolicyFromGroups({ id: props.policy.id, groupIds: assignedGroupIds }).unwrap();
                didUnassign = true;
            } catch (error) {
                showErrorToast(getErrorMessage(error));
                return;
            }
        }

        try {
            await deletePolicy({ id: props.policy.id }).unwrap();
        } catch (error) {
            showErrorToast(getErrorMessage(error));
            if (didUnassign) {
                showErrorToast(t("modals:deletePolicy.toast.deleteFailedAfterUnassign", { policyName: props.policy.name }));
            }
            return;
        }

        showSuccessToast(t("modals:deletePolicy.toast.deleteSuccess"));
        props.onDeleted();
        props.onClose();
    };

    return (
        <Modal
            open={props.open}
            title={t("modals:deletePolicy.title")}
            onClose={props.onClose}
            endButton={
                <DangerButton onClick={onSubmit} loading={isUnassigning || isDeleting} disabled={isUnassigning || isDeleting}>
                    {t("common:button.delete")}
                </DangerButton>
            }
        >
            <Typography>
                <Trans
                    ns="modals"
                    i18nKey="deletePolicy.description"
                    values={{ policyName: props.policy.name }}
                    components={{ bold: <strong /> }}
                />
            </Typography>
            {assignedGroupIds && assignedGroupIds.length > 0 ? (
                <CustomAlert severity="warning">
                    <DialogContentText>
                        {t("modals:deletePolicy.assignedGroupsWarning", { count: assignedGroupIds.length })}
                    </DialogContentText>
                </CustomAlert>
            ) : null}
        </Modal>
    );
};

export default DeletePolicyModal;
